<template>
  <InputSelectWithCheckboxes
    v-model="localTeamIds"
    data-testid="selector"
    :disable-options="allSelected"
    :label="getLocaleText('organisation_teams_selector.label')"
    :min-selected="1"
    :options="teamOptions"
    :placeholder-text="placeholderText"
    searchable
    :selected-text="selectedText"
    show-ok-button
    show-select-all-option
    @confirm="handleConfirm"
  >
    <template #afterSearchInput>
      <div
        class="tw:flex tw:items-center tw:justify-between tw:pl-4 tw:pr-2 tw:py-2 tw:border-b tw:border-denim-300 tw:cursor-pointer tw:hover:bg-denim-100"
        data-testid="select-all-label"
        @click.left.stop.prevent="handleClickAll"
      >
        <div>
          {{
            getLocaleText("organisation_teams_selector.visible_to_all_teams")
          }}
        </div>

        <div>
          <BaseCheckbox
            v-model="allSelected"
            data-testid="select-all-checkbox"
            dense
            new-design
          />
        </div>
      </div>
    </template>
  </InputSelectWithCheckboxes>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import BaseCheckbox from "shared/components/core/forms/BaseCheckbox.vue";
import InputSelectWithCheckboxes from "shared/components/core/forms/InputSelectWithCheckboxes.vue";
import useTeamPicker from "shared/composables/useTeamPicker";

const { organisationTeamOptions } = useTeamPicker();

const teamIds = defineModel<number[]>("teamIds", {
  required: true,
});

// If all teams are selected on mount, set allSelected to true.
const allSelected = ref<boolean>(
  teamIds.value.length === organisationTeamOptions.value.length
);

const localTeamIds = ref<number[]>([...teamIds.value]);

watch(allSelected, (newValue) => {
  if (newValue) {
    const allTeamIds = organisationTeamOptions.value.map((team) => team.id);

    localTeamIds.value = allTeamIds;
  } else {
    localTeamIds.value = [...teamIds.value];
  }
});

const teamOptions = computed(() =>
  organisationTeamOptions.value.map((team) => ({
    value: team.id,
    label: team.name,
  }))
);

const placeholderText = computed(() =>
  allSelected.value
    ? getLocaleText("organisation_teams_selector.all_teams")
    : getLocaleText("organisation_teams_selector.select_teams")
);

function selectedText(count: number) {
  if (allSelected.value) {
    return getLocaleText("organisation_teams_selector.visible_to_all_teams");
  }

  return getLocaleText("organisation_teams_selector.teams_count", { count });
}

function handleClickAll() {
  allSelected.value = !allSelected.value;
}

function handleConfirm() {
  teamIds.value = [...localTeamIds.value];
}
</script>
